import axios, { AxiosInstance, AxiosError } from 'axios';
import {
    Language,
    TranslationRequest,
    TranslationStatus,
    ApiResponse,
    LanguagesApiResponse,
    ServerLanguage,
    TranslateRequest,
    TranslateRequestPayload,
    TranslateResponse,
    WordCountResponse,
    CorporateLoginRequest,
    CorporateLoginResponse,
    IndividualLoginRequest,
    IndividualLoginResponse,
    SignupRequest,
    SignupResponse,
    AuthLoginRequest,
    AuthLoginResponse,
    Subscription,
    SubscriptionCreate,
    SubscriptionUpdate,
    CompanyUserCreateRequest,
    CompanyUser,
    AdminLoginRequest,
    AdminLoginResponse,
    ConfirmResponse,
    Invoice,
} from '../types';
import logger from '../utils/logger';
import { getAdminToken, getCorporateToken, getIndividualToken, clearAllAuth } from '../utils/auth';
import { fileToTranslateFileData } from '../utils/fileEncoding';
import toast from 'react-hot-toast';
import { clearIndividualAuthData, clearCorporateAuthData, clearAdminAuthData } from '../utils/sessionStorage';

class ApiService {
    private client: AxiosInstance;

    constructor() {
        const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        logger.log('🌐 ApiService initialized:', {
            baseURL,
            timestamp: new Date().toISOString(),
        });

        this.client = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Request interceptor for auth token injection and logging
        this.client.interceptors.request.use(
            (config) => {
                // 🔍 DEBUG: Log EVERY request attempt
                console.log('🔍 [INTERCEPTOR] ========================================');
                console.log('🔍 [INTERCEPTOR] Request intercepted:', config.url);
                console.log('🔍 [INTERCEPTOR] Method:', config.method?.toUpperCase());
                console.log('🔍 [INTERCEPTOR] Timestamp:', new Date().toISOString());

                // Skip token injection for login endpoints
                const isLoginEndpoint = config.url?.includes('/login/') || config.url?.includes('/signup');
                console.log('🔍 [INTERCEPTOR] Is login endpoint:', isLoginEndpoint);

                if (!isLoginEndpoint) {
                    // Skip token injection if explicitly requested (e.g., when passing captured token directly)
                    if ((config as any)._skipTokenInjection) {
                        console.log('🔍 [INTERCEPTOR] Skipping token injection (_skipTokenInjection=true)');
                        return config;
                    }

                    // Debug localStorage state
                    console.log('🔍 [INTERCEPTOR] Checking localStorage...');
                    console.log('🔍 [INTERCEPTOR] All localStorage keys:', Object.keys(localStorage));

                    // Inject Authorization header from localStorage
                    // Check admin, corporate, and individual tokens (admin takes precedence)
                    const adminToken = getAdminToken();
                    const corporateToken = getCorporateToken();
                    const individualToken = getIndividualToken();

                    const token = adminToken || corporateToken || individualToken;

                    if (token && config.headers) {
                        // Defensive: Only add "Bearer " prefix if not already present
                        const hasBearer = token.startsWith('Bearer ');

                        config.headers.Authorization = hasBearer ? token : `Bearer ${token}`;

                        const tokenType = adminToken ? 'admin' : corporateToken ? 'corporate' : 'individual';
                        (config as any)._tokenTypeUsed = tokenType;
                        console.log('🔐 [INTERCEPTOR] ✅ Authorization header SET');
                        logger.log(`🔐 Auth token injected into request (${tokenType})`);
                    } else {
                        console.warn('⚠️ [INTERCEPTOR] ❌ NO TOKEN - Authorization header NOT set');
                        console.warn('⚠️ [INTERCEPTOR] Token exists:', !!token);
                        console.warn('⚠️ [INTERCEPTOR] Headers object exists:', !!config.headers);
                        console.warn('⚠️ [INTERCEPTOR] This request will be UNAUTHENTICATED');
                    }
                } else {
                    console.log(`🚫 [INTERCEPTOR] Skipping token injection for login endpoint: ${config.url}`);
                    logger.log(`🚫 Skipping token injection for login endpoint: ${config.url}`);
                }
                console.log('🔍 [INTERCEPTOR] ========================================');

                logger.group(`📤 API REQUEST: ${config.method?.toUpperCase()} ${config.url}`);
                logger.log('📋 Request details:', {
                    url: config.url,
                    method: config.method,
                    headers: {
                        ...config.headers,
                        Authorization: config.headers?.Authorization ? '***Bearer [REDACTED]***' : undefined,
                    },
                    data: config.data,
                    params: config.params,
                    timestamp: new Date().toISOString(),
                });
                logger.groupEnd();
                return config;
            },
            (error) => {
                logger.error('💥 Request interceptor error:', error);
                return Promise.reject(error);
            },
        );

        // Response interceptor for error handling and logging
        this.client.interceptors.response.use(
            (response) => {
                logger.group(
                    `📨 API RESPONSE: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
                );
                logger.log('✅ Response details:', {
                    status: response.status,
                    statusText: response.statusText,
                    data: response.data,
                    headers: response.headers,
                    timestamp: new Date().toISOString(),
                });
                logger.groupEnd();
                return response;
            },
            (error: AxiosError) => {
                logger.group(
                    `❌ API ERROR: ${error.response?.status || 'Network'} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
                );
                logger.error('💥 API Error details:', {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    message: error.message,
                    code: error.code,
                    timestamp: new Date().toISOString(),
                });
                logger.groupEnd();

                // Handle 401 Unauthorized - clear session and redirect to login
                // BUT: Don't redirect if we're already on the login/signup page (expected 401 for invalid credentials)
                if (error.response?.status === 401) {
                    // Skip destructive actions (clearing tokens, redirecting) for critical requests
                    if ((error.config as any)?._skipDestructive401) {
                        console.warn(
                            '⚠️ [401 HANDLER] _skipDestructive401=true - skipping token clearing and redirect',
                        );
                        logger.warn('⚠️ 401 on critical request - NOT clearing session');
                        return Promise.reject(this.handleApiError(error));
                    }

                    logger.warn('🚪 401 Unauthorized - Checking context for session clearing');

                    // 🔍 DEBUG: Log full auth state BEFORE any action
                    console.error('🔴 ========================================');
                    console.error('🔴 401 UNAUTHORIZED ERROR CAUGHT');
                    console.error('🔴 ========================================');
                    console.error('🔴 Request URL:', error.config?.url);
                    console.error('🔴 Request Method:', error.config?.method);
                    console.error('🔴 Current Path:', window.location.pathname);
                    console.error('🔴 localStorage keys:', Object.keys(localStorage));
                    console.error('🔴 ========================================');

                    const isLoginPage =
                        window.location.pathname === '/login' ||
                        window.location.pathname === '/signup' ||
                        window.location.pathname === '/auth';

                    if (isLoginPage) {
                        // On login/signup pages, 401 is expected for invalid credentials
                        // Don't clear session or redirect, just pass the error through
                        logger.log('📍 401 on login page - passing through for form error handling');
                        console.log('📍 401 on login page - NOT redirecting');
                    } else {
                        // On other pages, 401 means session expired, clear and redirect
                        logger.warn('🔄 401 on protected page - clearing session and redirecting to login');
                        console.warn('🔄 About to clear session and redirect...');

                        // Show toast notification instead of blocking alert
                        toast.error('Your session has expired. Please log in again.', {
                            duration: 5000,
                            position: 'top-right',
                        });

                        console.error('🔴 401 UNAUTHORIZED - Session expired');
                        console.error('🔴 Request:', error.config?.method, error.config?.url);
                        console.error('🔴 Current Path:', window.location.pathname);

                        // Scope token clearing based on which token type was used for this request
                        const tokenTypeUsed = (error.config as any)?._tokenTypeUsed;
                        if (tokenTypeUsed === 'admin') {
                            clearAdminAuthData();
                        } else if (tokenTypeUsed === 'corporate') {
                            clearCorporateAuthData();
                        } else if (tokenTypeUsed === 'individual') {
                            clearIndividualAuthData();
                        } else {
                            // Fallback: clear all if token type unknown (backward compat)
                            clearAllAuth();
                        }

                        console.error('🔴 Session cleared. Redirecting to home page...');

                        // Redirect to home page (where login modal can be opened)
                        if (window.location.pathname !== '/') {
                            window.location.href = '/';
                        }
                    }
                }

                return Promise.reject(this.handleApiError(error));
            },
        );
    }

    /**
     * Handle API errors with specific user-friendly messages
     * @param error - Axios error object
     * @returns Error with specific message
     */
    private handleApiError(error: AxiosError): Error {
        if (error.response) {
            const status = error.response.status;
            const errorData = error.response.data as any;

            switch (status) {
                case 400:
                    return new Error(
                        errorData?.message || errorData?.detail || 'Invalid request. Please check your input.',
                    );
                case 401:
                    return new Error('Invalid request. Please try again.');
                case 403:
                    return new Error('Request denied. Please try again.');
                case 404:
                    return new Error('Resource not found. Please try again.');
                case 408:
                    return new Error(
                        'Request timeout. The server took too long to process your files. Please try again with smaller files or fewer files at once.',
                    );
                case 413:
                    return new Error('File too large. Please reduce file size and try again.');
                case 422:
                    // Handle validation errors from new API
                    return new Error(errorData?.message || 'Validation error. Please check your input.');
                case 429:
                    return new Error('Too many requests. Please try again in a few minutes.');
                case 500:
                    return new Error(errorData?.message || 'Server error. Please try again later.');
                case 502:
                case 503:
                case 504:
                    return new Error('Service temporarily unavailable. Please try again later.');
                default:
                    return new Error(errorData?.message || 'An unexpected error occurred.');
            }
        } else if (error.request) {
            // Network errors include CORS issues, timeouts, and connection failures
            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                return new Error('Request timeout. Please try again with smaller or fewer files.');
            }
            if (error.message.includes('Network Error') || error.message.includes('CORS')) {
                return new Error('Network error. Please check your internet connection and ensure you are logged in.');
            }
            return new Error('Network error. Please check your internet connection.');
        } else {
            return new Error('Request failed. Please try again.');
        }
    }

    async getLanguages(): Promise<Language[]> {
        logger.group('🌍 GET LANGUAGES API CALL');
        logger.log('🚀 Fetching languages from API...');

        try {
            const response = await this.client.get<LanguagesApiResponse>('/api/v1/languages');
            logger.log('📨 Languages API response received');

            if (!response.data.success || !response.data.data) {
                logger.error('❌ Invalid API response format:', response.data);
                throw new Error('Invalid API response format');
            }

            logger.log('📋 Converting server language format...', {
                serverLanguageCount: response.data.data.length,
            });

            // Convert server language format to our internal format
            const languages: Language[] = response.data.data.map((serverLang: ServerLanguage) => ({
                code: serverLang.code,
                name: serverLang.name,
                native_name: serverLang.name, // Use name as native_name fallback
                isPopular: this.isPopularLanguage(serverLang.code),
            }));

            logger.log('✅ Languages processed successfully:', {
                totalLanguages: languages.length,
                popularLanguages: languages.filter((l) => l.isPopular).length,
            });
            logger.groupEnd();
            return languages;
        } catch (error) {
            logger.error('💥 Error fetching languages:', {
                error: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Helper method to determine popular languages
    private isPopularLanguage(code: string): boolean {
        const popularCodes = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'zh', 'ja', 'ko', 'ar', 'hi'];
        return popularCodes.includes(code);
    }

    // Word count for quote modal
    async wordCount(files: File[]): Promise<WordCountResponse> {
        logger.group('📊 WORD COUNT API CALL');
        logger.log('🚀 Starting word count request...', {
            filesCount: files.length,
            fileDetails: files.map((f) => ({
                name: f.name,
                size: f.size,
                type: f.type,
            })),
            timestamp: new Date().toISOString(),
        });

        // Create FormData for file upload
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file);
        });

        try {
            logger.log('📤 Sending word count request to /api/word-count...', {
                filesCount: files.length,
            });

            // Dynamic timeout: 2 minutes base + 30 seconds per file, max 10 minutes
            const dynamicTimeout = Math.min(
                120000 + files.length * 30000,
                600000  // 10-minute ceiling
            );
            logger.log(`⏱️  Request timeout: ${dynamicTimeout / 1000}s`);

            const response = await this.client.post<WordCountResponse>('/api/word-count', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: dynamicTimeout,
            });

            logger.log('✅ Word count request successful:', {
                total_files: response.data.total_files,
                successful_files: response.data.successful_files,
                total_words: response.data.total_words,
                total_pages: response.data.total_pages,
                timestamp: new Date().toISOString(),
            });

            logger.groupEnd();
            return response.data;
        } catch (error) {
            logger.error('💥 Word count request failed:', {
                error: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Translation
    async initiateTranslation(request: TranslationRequest): Promise<TranslationStatus> {
        logger.group('🌍 TRANSLATION INITIATION API CALL');
        logger.log('🚀 Initiating translation...', {
            sourceLanguage: request.sourceLanguage,
            targetLanguage: request.targetLanguage,
            email: request.email,
            filesCount: request.files.length,
            fileDetails: request.files.map((f) => ({
                name: f.name,
                size: f.size,
                type: f.type,
            })),
            timestamp: new Date().toISOString(),
        });

        try {
            logger.log('📤 Sending translation request to /api/translate...');
            const response = await this.client.post<ApiResponse<TranslationStatus>>('/api/translate', request);

            if (!response.data.data) {
                throw new Error('Translation initiation failed: no data returned');
            }
            logger.log('✅ Translation initiation successful:', {
                status: response.data.data.status,
                hasPaymentInfo: !!response.data.data.payment_info,
                paymentInfo: response.data.data.payment_info
                    ? {
                          session_id: response.data.data.payment_info.session_id,
                          file_ids: response.data.data.payment_info.file_ids,
                          amount: response.data.data.payment_info.amount,
                          currency: response.data.data.payment_info.currency,
                          estimated_pages: response.data.data.payment_info.estimated_pages,
                      }
                    : null,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            return response.data.data;
        } catch (error) {
            logger.error('💥 Error initiating translation:', {
                error: error instanceof Error ? error.message : String(error),
                request: {
                    sourceLanguage: request.sourceLanguage,
                    targetLanguage: request.targetLanguage,
                    filesCount: request.files.length,
                },
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Payment - Type Definitions and Guards

    /**
     * Payment intent response from backend
     */
    private isPaymentIntentResponse(data: any): data is { clientSecret: string; paymentIntentId: string } {
        return (
            typeof data === 'object' &&
            data !== null &&
            typeof data.clientSecret === 'string' &&
            typeof data.paymentIntentId === 'string' &&
            data.clientSecret.length > 0 &&
            data.paymentIntentId.length > 0
        );
    }

    /**
     * Payment notification response from backend
     */
    private isPaymentNotificationResponse(data: any): data is {
        success: boolean;
        message?: string;
        data?: {
            customer_email?: string;
            payment_intent_id?: string;
            transaction_id?: string;
            status?: string;
            processing_time_ms?: number;
            total_files?: number;
            deleted_successfully?: number;
        };
    } {
        // Validate root structure
        if (typeof data !== 'object' || data === null) {
            return false;
        }

        if (typeof data.success !== 'boolean') {
            return false;
        }

        // Validate nested data structure if present
        if (data.data !== undefined && data.data !== null) {
            if (typeof data.data !== 'object') {
                return false;
            }

            // Validate critical fields when present
            const { transaction_id, payment_intent_id, status } = data.data;

            if (transaction_id !== undefined && typeof transaction_id !== 'string') {
                return false;
            }

            if (payment_intent_id !== undefined && typeof payment_intent_id !== 'string') {
                return false;
            }

            if (status !== undefined && typeof status !== 'string') {
                return false;
            }
        }

        return true;
    }

    /**
     * Handle Axios errors with meaningful messages
     */
    private handlePaymentError(error: unknown, operation: string): never {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            const detail = error.response?.data?.detail || error.response?.data?.message;
            const message = detail || error.message || 'Unknown error';

            logger.error(`${operation} failed [${status}]:`, {
                status,
                detail,
                url: error.config?.url,
                method: error.config?.method,
            });

            throw new Error(`${operation} failed: ${message}`);
        }

        logger.error(`${operation} unexpected error:`, error);
        throw error instanceof Error ? error : new Error(`${operation} failed`);
    }

    // Payment
    async createPaymentIntent(
        amount: number,
        currency: string = 'usd',
        options?: {
            idempotencyKey?: string;
            metadata?: Record<string, string>;
            description?: string;
        },
    ): Promise<{ clientSecret: string; paymentIntentId: string }> {
        try {
            // Validate amount server-side as well
            if (amount < 50 || amount > 1000000) {
                // 50 cents to $10,000
                throw new Error('Invalid payment amount');
            }

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };

            if (options?.idempotencyKey) {
                const key = options.idempotencyKey.trim();

                // Validate idempotency key format
                // Custom format: prefix_timestamp_random (e.g., stripe_1234_abc, pay_1234_xyz)
                // OR RFC 4122 UUID
                const validPattern =
                    /^([a-z]+_\d+_[a-z0-9]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

                if (!key || !validPattern.test(key)) {
                    throw new Error('Invalid idempotency key format');
                }

                headers['Idempotency-Key'] = key;
            }

            // Add CSRF protection
            const csrfToken = this.getCSRFToken();
            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken;
            }

            const response = await this.client.post<{
                clientSecret: string;
                paymentIntentId: string;
            }>(
                '/api/payment/create-intent',
                {
                    amount: amount, // Amount already in cents as integer from caller
                    currency: currency.toLowerCase(),
                    metadata: {
                        // Removed timestamp - causes Stripe idempotency errors when same key reused
                        // (React Strict Mode double-invocation creates different timestamps)
                        userAgent: navigator.userAgent.substring(0, 200), // Limit length
                        ...options?.metadata,
                    },
                    description: options?.description || 'Translation Service Payment',
                    // Add receipt email if available
                    receipt_email: this.getReceiptEmail(),
                },
                { headers },
            );

            // Validate response structure
            if (!response.data) {
                throw new Error('Empty response from payment API');
            }

            if (!this.isPaymentIntentResponse(response.data)) {
                logger.error('Invalid payment intent response structure:', response.data);
                throw new Error('Invalid payment intent response structure');
            }

            logger.log('✅ Payment intent created successfully:', {
                paymentIntentId: response.data.paymentIntentId,
                hasClientSecret: !!response.data.clientSecret,
            });

            return response.data;
        } catch (error) {
            this.handlePaymentError(error, 'Create payment intent');
        }
    }

    // Payment session registration
    async registerPaymentSession(
        fileIds: string[],
        sessionId: string,
    ): Promise<{
        success: boolean;
        session_id: string;
        file_ids: string[];
        amount: number;
        currency: string;
    }> {
        logger.group('📄 PAYMENT SESSION REGISTRATION API CALL');
        logger.log('🚀 Registering payment session...', {
            fileIds,
            sessionId,
            timestamp: new Date().toISOString(),
        });

        try {
            const requestPayload = {
                file_ids: fileIds,
                session_id: sessionId,
            };
            logger.log('📤 Session registration payload:', requestPayload);

            const response = await this.client.post<
                ApiResponse<{
                    success: boolean;
                    session_id: string;
                    file_ids: string[];
                    amount: number;
                    currency: string;
                }>
            >('/api/payment/register-session', requestPayload);

            if (!response.data.data) {
                throw new Error('Payment session registration failed: no data returned');
            }
            logger.log('✅ Payment session registered successfully:', {
                success: response.data.data.success,
                session_id: response.data.data.session_id,
                file_ids: response.data.data.file_ids,
                amount: response.data.data.amount,
                currency: response.data.data.currency,
            });
            logger.groupEnd();
            return response.data.data;
        } catch (error) {
            logger.error('💥 Error registering payment session:', {
                error: error instanceof Error ? error.message : String(error),
                fileIds,
                sessionId,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Contact form submission
    async submitContactForm(data: {
        name: string;
        email: string;
        subject: string;
        message: string;
    }): Promise<ApiResponse<{ success: boolean; message: string }>> {
        try {
            const response = await this.client.post('/api/contact', data);
            return {
                success: true,
                data: response.data,
            };
        } catch (error: any) {
            return {
                success: false,
                error: {
                    code: 'CONTACT_FORM_ERROR',
                    message: error.response?.data?.detail || 'Failed to send message',
                },
            };
        }
    }

    // Payment success/failure notification endpoints
    async notifyPaymentSuccess(paymentData: {
        customerEmail?: string;
        paymentIntentId?: string;
        stripe_checkout_session_id?: string;
        orderId?: string;
        amount: number;
        currency: string;
        paymentMethod: 'stripe' | 'square';
        metadata?: Record<string, any>;
        fileIds?: string[]; // File IDs from upload (eliminates Drive search)
    }): Promise<{
        success: boolean;
        message?: string;
        data?: {
            customer_email?: string;
            payment_intent_id?: string;
            transaction_id?: string;
            status?: string;
            processing_time_ms?: number;
        };
    }> {
        logger.group('✅ PAYMENT SUCCESS NOTIFICATION API CALL');
        logger.log('🚀 Notifying backend of payment success...', {
            paymentMethod: paymentData.paymentMethod,
            customerEmail: paymentData.customerEmail,
            paymentIntentId: paymentData.paymentIntentId,
            stripe_checkout_session_id: paymentData.stripe_checkout_session_id,
            orderId: paymentData.orderId,
            amount: paymentData.amount,
            currency: paymentData.currency,
            hasMetadata: !!paymentData.metadata,
            timestamp: new Date().toISOString(),
        });

        try {
            // Build request payload matching backend schema
            const requestPayload: Record<string, any> = {
                amount: paymentData.amount,
                currency: paymentData.currency,
                paymentMethod: paymentData.paymentMethod,
                timestamp: new Date().toISOString(),
            };

            // Add optional fields if present
            if (paymentData.customerEmail) {
                requestPayload.customerEmail = paymentData.customerEmail;
            }
            if (paymentData.paymentIntentId) {
                requestPayload.paymentIntentId = paymentData.paymentIntentId;
            }
            if (paymentData.stripe_checkout_session_id) {
                requestPayload.stripe_checkout_session_id = paymentData.stripe_checkout_session_id;
            }
            if (paymentData.orderId) {
                requestPayload.orderId = paymentData.orderId;
            }
            if (paymentData.metadata) {
                requestPayload.metadata = paymentData.metadata;
            }
            if (paymentData.fileIds && paymentData.fileIds.length > 0) {
                requestPayload.fileIds = paymentData.fileIds;
            }

            logger.log('📤 Payment success notification payload:', requestPayload);

            const response = await this.client.post<{
                success: boolean;
                message?: string;
                data?: Record<string, any>;
            }>('/api/payment/success', requestPayload);

            // Validate response structure
            if (!response.data) {
                throw new Error('Empty response from payment success API');
            }

            if (!this.isPaymentNotificationResponse(response.data)) {
                logger.error('Invalid payment notification response structure:', response.data);
                throw new Error('Invalid payment notification response structure');
            }

            // CRITICAL: Validate transaction_id exists for successful payments
            if (response.data.success && !response.data.data?.transaction_id) {
                logger.error('Payment notification succeeded but missing transaction_id:', response.data);
                throw new Error('Backend confirmation missing transaction ID - contact support');
            }

            logger.log('✅ Payment success notification sent successfully:', {
                success: response.data.success,
                message: response.data.message,
                hasTransactionId: !!response.data.data?.transaction_id,
                transactionId: response.data.data?.transaction_id,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            // Return full response including data field (contains transaction_id)
            return {
                success: response.data.success,
                message: response.data.message,
                data: response.data.data,
            };
        } catch (error) {
            logger.groupEnd();
            this.handlePaymentError(error, 'Notify payment success');
        }
    }

    async notifyPaymentFailure(failureData: {
        paymentIntentId?: string;
        orderId?: string;
        amount: number;
        currency: string;
        paymentMethod: 'stripe' | 'square';
        errorCode?: string;
        errorMessage?: string;
        metadata?: Record<string, any>;
    }): Promise<{
        success: boolean;
        message?: string;
        data?: {
            customer_email?: string;
            payment_intent_id?: string;
            total_files?: number;
            deleted_successfully?: number;
        };
    }> {
        logger.group('❌ PAYMENT FAILURE NOTIFICATION API CALL');
        logger.log('🚀 Notifying backend of payment failure...', {
            paymentMethod: failureData.paymentMethod,
            paymentIntentId: failureData.paymentIntentId,
            orderId: failureData.orderId,
            amount: failureData.amount,
            currency: failureData.currency,
            errorCode: failureData.errorCode,
            errorMessage: failureData.errorMessage,
            hasMetadata: !!failureData.metadata,
            timestamp: new Date().toISOString(),
        });

        try {
            const requestPayload = {
                customer_email: failureData.metadata?.email,
                payment_intent_id: failureData.paymentIntentId,
                timestamp: new Date().toISOString(),
            };
            logger.log('📤 Payment failure notification payload:', requestPayload);

            const response = await this.client.post<{
                success: boolean;
                message?: string;
                data?: Record<string, any>;
            }>('/api/payment/failure', requestPayload);

            // Validate response structure
            if (!response.data) {
                throw new Error('Empty response from payment failure API');
            }

            if (!this.isPaymentNotificationResponse(response.data)) {
                logger.error('Invalid payment notification response structure:', response.data);
                throw new Error('Invalid payment notification response structure');
            }

            logger.log('✅ Payment failure notification sent successfully:', {
                success: response.data.success,
                message: response.data.message,
                deletedFiles: response.data.data?.deleted_successfully,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            // Return full response including data field (contains cleanup info)
            return {
                success: response.data.success,
                message: response.data.message,
                data: response.data.data,
            };
        } catch (error) {
            logger.error('💥 Error notifying payment failure:', {
                error: error instanceof Error ? error.message : String(error),
                paymentMethod: failureData.paymentMethod,
                paymentIntentId: failureData.paymentIntentId,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            // Don't throw here as payment failure notification is not critical
            // Return graceful failure response
            return {
                success: false,
                message: error instanceof Error ? error.message : 'Failed to notify payment failure',
            };
        }
    }

    // Helper methods for security
    private getCSRFToken(): string | null {
        // Get CSRF token from meta tag or cookie
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        return metaTag ? metaTag.getAttribute('content') : null;
    }

    private getReceiptEmail(): string | null {
        // Get email from auth storage (individual_user key stores a JSON object)
        try {
            const raw = localStorage.getItem('individual_user');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const email = parsed?.user_email;
            return typeof email === 'string' && email.length > 0 ? email : null;
        } catch {
            return null;
        }
    }

    // Translate endpoint with file uploads
    async translateRequest(request: TranslateRequest): Promise<TranslateResponse> {
        logger.group('🌍 TRANSLATE REQUEST API CALL');
        logger.log('🚀 Starting translate request...', {
            sourceLanguage: request.sourceLanguage,
            targetLanguage: request.targetLanguage,
            email: request.email,
            filesCount: request.files.length,
            fileDetails: request.files.map((f) => ({
                name: f.name,
                size: f.size,
                type: f.type,
            })),
            hasPaymentIntentId: !!request.paymentIntentId,
            timestamp: new Date().toISOString(),
        });

        // Convert files to base64 for JSON transmission (avoiding encoding issues)
        logger.log('📋 Converting files to base64 for JSON transmission...');
        const fileData = await Promise.all(
            request.files.map((file, index) => {
                logger.log(`📄 Converting file ${index + 1}/${request.files.length}: ${file.name}`);
                return fileToTranslateFileData(file, index);
            }),
        );

        // Create JSON payload with file-level translation modes
        const payload: TranslateRequestPayload = {
            files: fileData,
            sourceLanguage: request.sourceLanguage,
            targetLanguage: request.targetLanguage,
            email: request.email,
            ...(request.paymentIntentId && { paymentIntentId: request.paymentIntentId }),
            ...(request.fileTranslationModes && { fileTranslationModes: request.fileTranslationModes }),
        };

        try {
            logger.log('📤 Sending translate request to /translate...', {
                filesCount: payload.files.length,
                totalSize: payload.files.reduce((acc, f) => acc + f.size, 0),
                payloadSizeEstimate: JSON.stringify(payload).length,
            });

            // Dynamic timeout: 2 minutes base + 30 seconds per file, max 10 minutes
            const dynamicTimeout = Math.min(
                120000 + (payload.files?.length ?? 1) * 30000,
                600000  // 10-minute ceiling
            );
            logger.log(`⏱️  Request timeout: ${dynamicTimeout / 1000}s`);

            const response = await this.client.post<TranslateResponse>('/translate', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
                timeout: dynamicTimeout,
            });

            logger.log('✅ Translate request successful:', {
                success: response.data.success,
                pricing: response.data.data?.pricing
                    ? {
                          total_pages: response.data.data.pricing.total_pages,
                          price_per_page: response.data.data.pricing.price_per_page,
                          total_amount: response.data.data.pricing.total_amount,
                          currency: response.data.data.pricing.currency,
                      }
                    : null,
                files: response.data.data?.files
                    ? {
                          total_files: response.data.data.files.total_files,
                          successful_uploads: response.data.data.files.successful_uploads,
                          failed_uploads: response.data.data.files.failed_uploads,
                      }
                    : null,
                payment: response.data.data?.payment
                    ? {
                          required: response.data.data.payment.required,
                          amount_cents: response.data.data.payment.amount_cents,
                          customer_email: response.data.data.payment.customer_email,
                      }
                    : null,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            return response.data;
        } catch (error) {
            logger.error('💥 Error in translate request:', {
                error: error instanceof Error ? error.message : String(error),
                errorCode: error instanceof AxiosError ? error.code : 'unknown',
                responseStatus: error instanceof AxiosError ? error.response?.status : undefined,
                request: {
                    sourceLanguage: request.sourceLanguage,
                    targetLanguage: request.targetLanguage,
                    filesCount: request.files.length,
                    email: request.email,
                },
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    /**
     * Translate request for individual users (no company, uses user_transactions collection)
     * Calls /translate-user endpoint instead of /translate
     */
    async translateUserRequest(request: TranslateRequest & { userName: string }): Promise<TranslateResponse> {
        logger.group('👤 TRANSLATE USER REQUEST API CALL');
        logger.log('🚀 Starting individual user translate request...', {
            sourceLanguage: request.sourceLanguage,
            targetLanguage: request.targetLanguage,
            email: request.email,
            userName: request.userName,
            filesCount: request.files.length,
            fileDetails: request.files.map((f) => ({
                name: f.name,
                size: f.size,
                type: f.type,
            })),
            hasPaymentIntentId: !!request.paymentIntentId,
            timestamp: new Date().toISOString(),
        });

        // Convert files to base64 for JSON transmission (same as translateRequest)
        logger.log('📋 Converting files to base64 for JSON transmission...');
        const fileData = await Promise.all(
            request.files.map((file, index) => {
                logger.log(`📄 Converting file ${index + 1}/${request.files.length}: ${file.name}`);
                return fileToTranslateFileData(file, index);
            }),
        );

        // Create JSON payload with userName and file-level translation modes
        const payload = {
            files: fileData,
            sourceLanguage: request.sourceLanguage,
            targetLanguage: request.targetLanguage,
            email: request.email,
            userName: request.userName,
            ...(request.paymentIntentId && { paymentIntentId: request.paymentIntentId }),
            ...(request.fileTranslationModes && { fileTranslationModes: request.fileTranslationModes }),
            ...(request.price_override && { price_override: request.price_override }),
        };

        try {
            logger.log('📤 Sending translate user request to /translate-user...', {
                filesCount: payload.files.length,
                totalSize: payload.files.reduce((acc, f) => acc + f.size, 0),
                payloadSizeEstimate: JSON.stringify(payload).length,
            });

            // Dynamic timeout: 2 minutes base + 30 seconds per file, max 10 minutes
            const dynamicTimeout = Math.min(
                120000 + (payload.files?.length ?? 1) * 30000,
                600000  // 10-minute ceiling
            );
            logger.log(`⏱️  Request timeout: ${dynamicTimeout / 1000}s`);

            const response = await this.client.post<TranslateResponse>('/translate-user', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
                timeout: dynamicTimeout,
            });

            logger.log('✅ Translate user request successful:', {
                success: response.data.success,
                pricing: response.data.data?.pricing,
                files: response.data.data?.files,
                payment: response.data.data?.payment,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            return response.data;
        } catch (error) {
            logger.error('💥 Error in translate user request:', {
                error: error instanceof Error ? error.message : String(error),
                errorCode: error instanceof AxiosError ? error.code : 'unknown',
                responseStatus: error instanceof AxiosError ? error.response?.status : undefined,
                request: {
                    sourceLanguage: request.sourceLanguage,
                    targetLanguage: request.targetLanguage,
                    filesCount: request.files.length,
                    email: request.email,
                    userName: request.userName,
                },
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Payment success confirmation
    async confirmPaymentSuccess(
        customerEmail: string,
        paymentIntentId: string,
    ): Promise<{
        success: boolean;
        message: string;
        data: {
            customer_email: string;
            payment_intent_id: string;
            total_files: number;
            moved_successfully: number;
            failed_moves?: number;
            inbox_folder_id?: string;
            files_moved?: number;
            files_found?: number;
            workflow: string;
        };
    }> {
        logger.group('✅ PAYMENT SUCCESS CONFIRMATION');
        logger.log('🎉 Confirming payment success...', {
            customerEmail,
            paymentIntentId,
            timestamp: new Date().toISOString(),
        });

        try {
            const payload = {
                customer_email: customerEmail,
                payment_intent_id: paymentIntentId,
            };

            logger.log('📤 Sending payment success payload:', payload);

            const response = await this.client.post<{
                success: boolean;
                message: string;
                data: {
                    customer_email: string;
                    payment_intent_id: string;
                    total_files: number;
                    moved_successfully: number;
                    failed_moves?: number;
                    inbox_folder_id?: string;
                    files_moved?: number;
                    files_found?: number;
                    workflow: string;
                };
            }>('/api/payment/success', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Payment success confirmed:', {
                success: response.data.success,
                message: response.data.message,
                data: response.data.data,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Payment success confirmation error:', {
                error: error instanceof Error ? error.message : String(error),
                customerEmail,
                paymentIntentId,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    /**
     * Confirm payment success for individual users (user_transactions)
     * Calls /api/payment/user-success endpoint
     */
    async confirmUserPaymentSuccess(
        customerEmail: string,
        squareTransactionId: string,
    ): Promise<{
        success: boolean;
        message: string;
        data: {
            customer_email: string;
            stripe_checkout_session_id: string;
            status: string;
            processing_time_ms: number;
        };
    }> {
        logger.group('✅ USER PAYMENT SUCCESS CONFIRMATION');
        logger.log('🎉 Confirming user payment success...', {
            customerEmail,
            squareTransactionId,
            timestamp: new Date().toISOString(),
        });

        try {
            const payload = {
                customerEmail: customerEmail,
                stripe_checkout_session_id: squareTransactionId,
                metadata: {
                    timestamp: new Date().toISOString(),
                },
            };

            logger.log('📤 Sending user payment success payload:', payload);

            const response = await this.client.post<{
                success: boolean;
                message: string;
                data: {
                    customer_email: string;
                    stripe_checkout_session_id: string;
                    status: string;
                    processing_time_ms: number;
                };
            }>('/api/payment/user-success', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ User payment success confirmed:', {
                success: response.data.success,
                message: response.data.message,
                data: response.data.data,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 User payment success confirmation error:', {
                error: error instanceof Error ? error.message : String(error),
                customerEmail,
                squareTransactionId,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Payment failure cleanup
    async confirmPaymentFailure(
        customerEmail: string,
        paymentIntentId: string,
    ): Promise<{
        success: boolean;
        message: string;
        data: {
            customer_email: string;
            payment_intent_id: string;
            total_files: number;
            deleted_successfully: number;
            workflow: string;
        };
    }> {
        logger.group('❌ PAYMENT FAILURE CLEANUP');
        logger.log('💸 Confirming payment failure...', {
            customerEmail,
            paymentIntentId,
            timestamp: new Date().toISOString(),
        });

        try {
            const params = new URLSearchParams({
                customer_email: customerEmail,
                payment_intent_id: paymentIntentId,
            });

            logger.log('📤 Sending payment failure request with params:', params.toString());

            const response = await this.client.post<{
                success: boolean;
                message: string;
                data: {
                    customer_email: string;
                    payment_intent_id: string;
                    total_files: number;
                    deleted_successfully: number;
                    workflow: string;
                };
            }>(
                `/api/payment/failure?${params.toString()}`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                    },
                },
            );

            logger.log('✅ Payment failure confirmed:', {
                success: response.data.success,
                message: response.data.message,
                data: response.data.data,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Payment failure confirmation error:', {
                error: error instanceof Error ? error.message : String(error),
                customerEmail,
                paymentIntentId,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Transaction Confirmation
    /**
     * @deprecated Use confirmEnterpriseTransaction() or confirmIndividualTransaction() instead.
     * This unified method will be removed in a future version.
     *
     * NOTE: Client sends payment info (stripe_checkout_session_id, status, transaction_id) AND file_ids
     * - transaction_id: From /translate-user response (stored in frontend state)
     * - Individual flow: Sends file_ids - server processes only specified files
     * - Enterprise flow: Empty/no file_ids - server falls back to search by email + status
     */
    async confirmTransactions(request: {
        stripe_checkout_session_id?: string;
        status?: boolean;
        transaction_id?: string;
        file_ids?: string[];
    }): Promise<{
        success: boolean;
        message: string;
        data: {
            confirmed_transactions: number;
            moved_files: number;
        };
    }> {
        logger.group('✅ TRANSACTION CONFIRMATION API CALL');
        logger.log('🔵 Confirming payment with transaction_id...', {
            stripe_checkout_session_id: request.stripe_checkout_session_id,
            transaction_id: request.transaction_id,
            status: request.status ?? true,
            file_ids_count: request.file_ids?.length ?? 0,
            file_ids: request.file_ids ?? [],
            flow: request.file_ids && request.file_ids.length > 0 ? 'Individual' : 'Enterprise',
            timestamp: new Date().toISOString(),
        });

        try {
            logger.log('📤 Sending confirmation request to /api/transactions/confirm...');
            if (request.file_ids && request.file_ids.length > 0) {
                logger.log('🔍 Individual flow: Request contains file_ids from upload response');
            } else {
                logger.log('🔍 Enterprise flow: Server will search by email + status');
            }
            logger.log('📦 Request payload:', request);

            const response = await this.client.post<{
                success: boolean;
                message: string;
                data: {
                    confirmed_transactions: number;
                    moved_files: number;
                };
            }>('/api/transactions/confirm', request, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Transactions confirmed successfully:', {
                success: response.data.success,
                message: response.data.message,
                confirmed_transactions: response.data.data.confirmed_transactions,
                moved_files: response.data.data.moved_files,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Transaction confirmation error:', {
                error: error instanceof Error ? error.message : String(error),
                stripe_checkout_session_id: request.stripe_checkout_session_id,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    /**
     * Confirm enterprise transaction
     * POST /api/transactions/confirm-enterprise
     *
     * @param params.transaction_id - Database transaction ID (optional, for existing transactions)
     * @param params.storage_id - Storage ID (optional, for deferred transaction creation)
     * @param params.status - Confirmation status (true for confirmed)
     * @returns ConfirmResponse with confirmation details
     */
    async confirmEnterpriseTransaction(params: {
        transaction_id?: string;
        storage_id?: string;
        status: boolean;
    }): Promise<ConfirmResponse> {
        logger.group('🏢 ENTERPRISE TRANSACTION CONFIRMATION API CALL');
        logger.log('🏢 ENTERPRISE: Confirming transaction...', {
            transaction_id: params.transaction_id,
            storage_id: params.storage_id,
            status: params.status,
            timestamp: new Date().toISOString(),
        });

        try {
            logger.log('📤 Sending confirmation request to /api/transactions/confirm-enterprise...');
            logger.log('📦 Request payload:', params);

            const response = await this.client.post<ConfirmResponse>('/api/transactions/confirm-enterprise', params, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Enterprise transaction confirmed successfully:', {
                success: response.data.success,
                message: response.data.message,
                confirmed_transactions: response.data.data.confirmed_transactions,
                moved_files: response.data.data.moved_files,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Enterprise transaction confirmation error:', {
                error: error instanceof Error ? error.message : String(error),
                transaction_id: params.transaction_id,
                storage_id: params.storage_id,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    /**
     * Confirm enterprise transaction V2 (Deferred Transaction Creation)
     * POST /api/transactions/confirm-enterprise-v2
     *
     * This is the NEW flow where transactions are NOT created during upload.
     * Instead, transactions are created when the user confirms.
     *
     * @param params.stored_files - Array of stored file info from upload
     * @param params.source_language - Source language code
     * @param params.target_language - Target language code
     * @param params.expected_total_pages - Total page count from pricing
     * @param params.expected_total_words - Total word count (optional)
     * @param params.file_translation_modes - Per-file translation modes (optional)
     * @param params.status - Confirmation status (true=confirm, false=cancel)
     * @returns ConfirmResponse with confirmation details and created transaction_id
     */
    async confirmEnterpriseTransactionV2(params: {
        stored_files: any[];
        source_language: string;
        target_language: string;
        expected_total_pages: number;
        expected_total_words?: number;
        file_translation_modes?: Array<{ fileName: string; translationMode: string }>;
        status: boolean;
    }): Promise<ConfirmResponse> {
        logger.group('🏢 ENTERPRISE TRANSACTION CONFIRMATION V2 API CALL');
        logger.log('🏢 ENTERPRISE V2: Confirming transaction with deferred creation...', {
            stored_files_count: params.stored_files.length,
            source_language: params.source_language,
            target_language: params.target_language,
            expected_total_pages: params.expected_total_pages,
            status: params.status,
            timestamp: new Date().toISOString(),
        });

        try {
            logger.log('📤 Sending V2 confirmation request to /api/transactions/confirm-enterprise-v2...');
            logger.log('📦 Request payload:', {
                ...params,
                stored_files: params.stored_files.map((f: any) => ({
                    file_id: f.file_id,
                    file_name: f.file_name,
                    pages: f.pages,
                })),
            });

            const response = await this.client.post<ConfirmResponse>(
                '/api/transactions/confirm-enterprise-v2',
                params,
                {
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                    },
                },
            );

            logger.log('✅ Enterprise V2 transaction confirmed successfully:', {
                success: response.data.success,
                message: response.data.message,
                confirmed_transactions: response.data.data.confirmed_transactions,
                moved_files: response.data.data.moved_files,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Enterprise V2 transaction confirmation error:', {
                error: error instanceof Error ? error.message : String(error),
                stored_files_count: params.stored_files.length,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    /**
     * Confirm individual user transaction
     * POST /api/transactions/confirm-individual
     *
     * @param params.transaction_id - Database transaction ID (USER######)
     * @param params.stripe_checkout_session_id - Payment provider transaction ID
     * @param params.file_ids - Array of file IDs to process
     * @param params.status - Confirmation status (true for confirmed)
     * @returns ConfirmResponse with confirmation details
     */
    async confirmIndividualTransaction(
        params: {
            transaction_id: string;
            stripe_checkout_session_id: string;
            file_ids: string[];
            status: boolean;
        },
        options?: { authToken?: string },
    ): Promise<ConfirmResponse> {
        logger.group('👤 INDIVIDUAL TRANSACTION CONFIRMATION API CALL');
        logger.log('👤 INDIVIDUAL: Confirming transaction...', {
            transaction_id: params.transaction_id,
            stripe_checkout_session_id: params.stripe_checkout_session_id,
            file_ids_count: params.file_ids.length,
            file_ids: params.file_ids,
            status: params.status,
            timestamp: new Date().toISOString(),
        });

        try {
            logger.log('📤 Sending confirmation request to /api/transactions/confirm-individual...');
            logger.log('📦 Request payload:', params);

            const requestConfig: any = {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
                _skipDestructive401: true,
            };

            // If a captured auth token is provided, inject it directly and skip interceptor token injection
            if (options?.authToken) {
                requestConfig.headers.Authorization = `Bearer ${options.authToken}`;
                requestConfig._skipTokenInjection = true;
            }

            const response = await this.client.post<ConfirmResponse>(
                '/api/transactions/confirm-individual',
                params,
                requestConfig,
            );

            logger.log('✅ Individual transaction confirmed successfully:', {
                success: response.data.success,
                message: response.data.message,
                confirmed_transactions: response.data.data.confirmed_transactions,
                moved_files: response.data.data.moved_files,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Individual transaction confirmation error:', {
                error: error instanceof Error ? error.message : String(error),
                transaction_id: params.transaction_id,
                stripe_checkout_session_id: params.stripe_checkout_session_id,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Transaction Decline
    async declineTransactions(request: { transaction_ids: string[] }): Promise<{
        success: boolean;
        message: string;
        data: {
            declined_transactions: number;
            deleted_files: number;
        };
    }> {
        logger.group('❌ TRANSACTION DECLINE API CALL');
        logger.log('🔴 Declining transactions...', {
            transaction_ids: request.transaction_ids,
            count: request.transaction_ids.length,
            timestamp: new Date().toISOString(),
        });

        try {
            logger.log('📤 Sending decline request to /api/transactions/decline...');

            const response = await this.client.post<{
                success: boolean;
                message: string;
                data: {
                    declined_transactions: number;
                    deleted_files: number;
                };
            }>('/api/transactions/decline', request, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Transactions declined successfully:', {
                success: response.data.success,
                message: response.data.message,
                declined_transactions: response.data.data.declined_transactions,
                deleted_files: response.data.data.deleted_files,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Transaction decline error:', {
                error: error instanceof Error ? error.message : String(error),
                transaction_ids: request.transaction_ids,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Corporate Login
    async corporateLogin(request: CorporateLoginRequest): Promise<CorporateLoginResponse> {
        logger.group('🏢 CORPORATE LOGIN API CALL');
        logger.log('🚀 Initiating corporate login...', {
            companyName: request.companyName,
            userFullName: request.userFullName,
            userEmail: request.userEmail,
            loginDateTime: request.loginDateTime,
            timestamp: new Date().toISOString(),
        });

        try {
            const payload = {
                companyName: request.companyName,
                password: request.password,
                userFullName: request.userFullName,
                userEmail: request.userEmail,
                loginDateTime: request.loginDateTime,
            };

            logger.log('📤 Sending corporate login request to /login/corporate...');

            const response = await this.client.post<CorporateLoginResponse>('/login/corporate', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Corporate login successful:', {
                success: response.data.success,
                message: response.data.message,
                authToken: response.data.data?.authToken ? '***' + response.data.data.authToken.slice(-8) : undefined,
                tokenType: response.data.data?.tokenType,
                expiresIn: response.data.data?.expiresIn,
                expiresAt: response.data.data?.expiresAt,
                user: response.data.data?.user,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Corporate login error:', {
                error: error instanceof Error ? error.message : String(error),
                companyName: request.companyName,
                userFullName: request.userFullName,
                userEmail: request.userEmail,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Individual Login
    async individualLogin(request: IndividualLoginRequest): Promise<IndividualLoginResponse> {
        logger.group('👤 INDIVIDUAL LOGIN API CALL');
        logger.log('🚀 Initiating individual login...', {
            userFullName: request.userFullName,
            userEmail: request.userEmail,
            loginDateTime: request.loginDateTime,
            timestamp: new Date().toISOString(),
        });

        try {
            const payload = {
                userFullName: request.userFullName,
                userEmail: request.userEmail,
                loginDateTime: request.loginDateTime,
            };

            logger.log('📤 Sending individual login request to /login/individual...');

            const response = await this.client.post<IndividualLoginResponse>('/login/individual', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Individual login successful:', {
                success: response.data.success,
                message: response.data.message,
                authToken: response.data.data?.authToken ? '***' + response.data.data.authToken.slice(-8) : undefined,
                tokenType: response.data.data?.tokenType,
                expiresIn: response.data.data?.expiresIn,
                expiresAt: response.data.data?.expiresAt,
                user: response.data.data?.user,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Individual login error:', {
                error: error instanceof Error ? error.message : String(error),
                userFullName: request.userFullName,
                userEmail: request.userEmail,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Auth API - Signup
    async signup(request: SignupRequest): Promise<SignupResponse> {
        logger.group('🔐 USER SIGNUP API CALL');
        logger.log('🚀 Initiating user signup...', {
            user_name: request.user_name,
            user_email: request.user_email,
            timestamp: new Date().toISOString(),
        });

        try {
            const payload = {
                user_name: request.user_name,
                user_email: request.user_email,
                password: request.password,
            };

            logger.log('📤 Sending signup request to /login/api/auth/signup...');

            const response = await this.client.post<SignupResponse>('/login/api/auth/signup', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ User signup successful:', {
                success: response.data.success,
                message: response.data.message,
                user_id: response.data.data?.user_id,
                user_name: response.data.data?.user_name,
                user_email: response.data.data?.user_email,
                created_at: response.data.data?.created_at,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 User signup error:', {
                error: error instanceof Error ? error.message : String(error),
                user_name: request.user_name,
                user_email: request.user_email,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Auth API - Login
    async authLogin(request: AuthLoginRequest): Promise<AuthLoginResponse> {
        logger.group('🔐 USER LOGIN API CALL');
        logger.log('🚀 Initiating user login...', {
            email: request.email,
            timestamp: new Date().toISOString(),
        });

        try {
            const payload = {
                email: request.email,
                password: request.password,
            };

            logger.log('📤 Sending login request to /login/api/auth/login...');

            const response = await this.client.post<AuthLoginResponse>('/login/api/auth/login', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ User login successful:', {
                success: response.data.success,
                message: response.data.message,
                user_id: response.data.data?.user_id,
                user_name: response.data.data?.user_name,
                user_email: response.data.data?.user_email,
                session_token: response.data.data?.session_token
                    ? '***' + response.data.data.session_token.slice(-8)
                    : undefined,
                token_type: response.data.data?.token_type,
                expires_in: response.data.data?.expires_in,
                expires_at: response.data.data?.expires_at,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 User login error:', {
                error: error instanceof Error ? error.message : String(error),
                email: request.email,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    // Admin Login
    async adminLogin(email: string, password: string): Promise<AdminLoginResponse> {
        logger.group('🔐 ADMIN LOGIN API CALL');
        logger.log('🚀 Initiating admin login...', {
            email,
            timestamp: new Date().toISOString(),
        });

        try {
            const payload: AdminLoginRequest = {
                email,
                password,
            };

            logger.log('📤 Sending admin login request to /login/admin...');

            const response = await this.client.post<AdminLoginResponse>('/login/admin', payload, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Admin login successful:', {
                success: response.data.success,
                message: response.data.message,
                authToken: response.data.data?.authToken ? '***' + response.data.data.authToken.slice(-8) : undefined,
                tokenType: response.data.data?.tokenType,
                expiresIn: response.data.data?.expiresIn,
                expiresAt: response.data.data?.expiresAt,
                user: response.data.data?.user,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();

            return response.data;
        } catch (error) {
            logger.error('💥 Admin login error:', {
                error: error instanceof Error ? error.message : String(error),
                email,
                timestamp: new Date().toISOString(),
            });
            logger.groupEnd();
            throw error;
        }
    }

    /**
     * Get all payments for admin dashboard
     * Fetches from /api/v1/payments/company/{company_name} endpoint
     */
    async getAllPayments(
        company_name?: string,
        status?: string,
        limit: number = 100,
        skip: number = 0,
    ): Promise<any[]> {
        try {
            logger.log('📊 Fetching all payments...', {
                company_name,
                status,
                limit,
                skip,
                timestamp: new Date().toISOString(),
            });

            // If company_name is provided, use company-specific endpoint
            if (company_name) {
                const params = new URLSearchParams();
                if (status) params.append('status', status);
                params.append('limit', limit.toString());
                params.append('skip', skip.toString());

                // URL encode company_name for spaces and special characters
                const encodedCompanyName = encodeURIComponent(company_name);
                const response = await this.client.get(
                    `/api/v1/payments/company/${encodedCompanyName}?${params.toString()}`,
                );

                logger.log('✅ Payments fetched successfully:', {
                    count: response.data.data?.count || 0,
                    timestamp: new Date().toISOString(),
                });

                return response.data.data?.payments || [];
            }

            // Otherwise, fetch all payments (would need a different endpoint)
            // For now, return empty array
            logger.warn('⚠️ No company_name provided, cannot fetch payments');
            return [];
        } catch (error) {
            logger.error('💥 Error fetching payments:', {
                error: error instanceof Error ? error.message : String(error),
                company_name,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get a single payment by ID
     * Fetches from /api/v1/payments/{payment_id} endpoint
     */
    async getPaymentById(payment_id: string): Promise<any> {
        try {
            logger.log('📄 Fetching payment by ID...', {
                payment_id,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.get(`/api/v1/payments/${payment_id}`);

            logger.log('✅ Payment fetched successfully:', {
                payment_id,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error fetching payment:', {
                error: error instanceof Error ? error.message : String(error),
                payment_id,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get all invoices for admin dashboard
     * Fetches from /api/v1/invoices/company/{company_name} endpoint
     */
    async getAllInvoices(
        company_name?: string,
        status?: string,
        limit: number = 100,
        skip: number = 0,
    ): Promise<any[]> {
        try {
            logger.log('📊 Fetching all invoices...', {
                company_name,
                status,
                limit,
                skip,
                timestamp: new Date().toISOString(),
            });

            // If company_name is provided, use company-specific endpoint
            if (company_name) {
                const params = new URLSearchParams();
                if (status) params.append('status', status);
                params.append('limit', limit.toString());
                params.append('skip', skip.toString());

                // URL encode company_name for spaces and special characters
                const encodedCompanyName = encodeURIComponent(company_name);
                const response = await this.client.get(
                    `/api/v1/invoices/company/${encodedCompanyName}?${params.toString()}`,
                );

                logger.log('✅ Invoices fetched successfully:', {
                    count: response.data.data?.count || 0,
                    timestamp: new Date().toISOString(),
                });

                return response.data.data?.invoices || [];
            }

            // Otherwise, return empty array (would need a different endpoint for all invoices)
            logger.warn('⚠️ No company_name provided, cannot fetch invoices');
            return [];
        } catch (error) {
            logger.error('💥 Error fetching invoices:', {
                error: error instanceof Error ? error.message : String(error),
                company_name,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get all unique companies that have translation transactions
     * Fetches from /api/v1/translation-transactions/companies endpoint
     */
    async getCompaniesWithTranslations(): Promise<string[]> {
        try {
            logger.log('📊 Fetching companies with translation transactions...', {
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.get('/api/v1/translation-transactions/companies');

            logger.log('✅ Companies fetched successfully:', {
                count: response.data.data?.count || 0,
                companies: response.data.data?.companies || [],
                timestamp: new Date().toISOString(),
            });

            return response.data.data?.companies || [];
        } catch (error) {
            logger.error('💥 Error fetching companies with translations:', {
                error: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get all companies with full details (address, contact info, etc.)
     * Fetches from /api/v1/companies endpoint
     */
    async getCompanies(): Promise<any[]> {
        try {
            logger.log('📊 Fetching all companies...', {
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.get('/api/v1/companies');

            logger.log('✅ Companies fetched successfully:', {
                count: response.data.data?.count || 0,
                timestamp: new Date().toISOString(),
            });

            return response.data.data?.companies || [];
        } catch (error) {
            logger.error('💥 Error fetching companies:', {
                error: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Create a new company
     * POST /api/v1/companies
     */
    async createCompany(companyData: any): Promise<any> {
        try {
            logger.log('🏢 CREATE COMPANY - REQUEST START:', {
                company_name: companyData.company_name,
                timestamp: new Date().toISOString(),
            });

            logger.log('📨 Request Data:', {
                ...companyData,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post('/api/v1/companies', companyData);

            logger.log('✅ CREATE COMPANY - RESPONSE SUCCESS:', {
                company_name: response.data.data?.company_name,
                _id: response.data.data?._id,
                timestamp: new Date().toISOString(),
            });

            return response.data.data;
        } catch (error) {
            logger.error('💥 CREATE COMPANY - ERROR:', {
                error: error instanceof Error ? error.message : String(error),
                company_name: companyData.company_name,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Update an existing company
     * PATCH /api/v1/companies/{company_name}
     */
    async updateCompany(companyName: string, companyData: any): Promise<any> {
        try {
            logger.log('🏢 UPDATE COMPANY - REQUEST START:', {
                method: 'PATCH',
                url: `/api/v1/companies/${encodeURIComponent(companyName)}`,
                companyName,
                payload: companyData,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.patch(
                `/api/v1/companies/${encodeURIComponent(companyName)}`,
                companyData,
                {
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                    },
                },
            );

            logger.log('✅ UPDATE COMPANY - RESPONSE SUCCESS:', {
                companyName,
                status: response.status,
                statusText: response.statusText,
                data: response.data,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            const axiosError = error as any;
            logger.error('💥 UPDATE COMPANY - ERROR:', {
                companyName,
                error: error instanceof Error ? error.message : String(error),
                response: {
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    data: axiosError.response?.data,
                    headers: axiosError.response?.headers,
                },
                request: {
                    url: axiosError.config?.url,
                    method: axiosError.config?.method,
                    data: axiosError.config?.data,
                },
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get all translation transactions for admin dashboard
     * Fetches from /api/v1/translation-transactions/company/{company_name} endpoint
     */
    async getAllTranslationTransactions(
        company_name?: string,
        status?: string,
        limit: number = 100,
        skip: number = 0,
    ): Promise<any[]> {
        try {
            logger.log('📊 Fetching all translation transactions...', {
                company_name,
                status,
                limit,
                skip,
                timestamp: new Date().toISOString(),
            });

            // If company_name is provided, use company-specific endpoint
            if (company_name) {
                const params = new URLSearchParams();
                if (status) params.append('status', status);
                params.append('limit', limit.toString());
                params.append('skip', skip.toString());

                // URL encode company_name for spaces and special characters
                const encodedCompanyName = encodeURIComponent(company_name);
                const response = await this.client.get(
                    `/api/v1/translation-transactions/company/${encodedCompanyName}?${params.toString()}`,
                );

                logger.log('✅ Translation transactions fetched successfully:', {
                    count: response.data.data?.count || 0,
                    timestamp: new Date().toISOString(),
                });

                return response.data.data?.transactions || [];
            }

            // Otherwise, return empty array (would need a different endpoint for all transactions)
            logger.warn('⚠️ No company_name provided, cannot fetch translation transactions');
            return [];
        } catch (error) {
            logger.error('💥 Error fetching translation transactions:', {
                error: error instanceof Error ? error.message : String(error),
                company_name,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get all user transactions for admin dashboard
     * Fetches from /api/v1/user-transactions/user/{user_email} endpoint
     */
    async getAllUserTransactions(
        user_email?: string,
        status?: string,
        limit: number = 100,
        skip: number = 0,
    ): Promise<any[]> {
        try {
            logger.log('📊 Fetching all user transactions...', {
                user_email,
                status,
                limit,
                skip,
                timestamp: new Date().toISOString(),
            });

            // If user_email is provided, use user-specific endpoint
            if (user_email) {
                const params = new URLSearchParams();
                if (status) params.append('status', status);
                params.append('limit', limit.toString());
                params.append('skip', skip.toString());

                const response = await this.client.get(
                    `/api/v1/user-transactions/user/${user_email}?${params.toString()}`,
                );

                logger.log('✅ User transactions fetched successfully:', {
                    count: response.data.data?.count || 0,
                    timestamp: new Date().toISOString(),
                });

                return response.data.data?.transactions || [];
            }

            // Fetch ALL user transactions (no email filter)
            logger.log('📊 Fetching ALL user transactions from database...');
            const params = new URLSearchParams();
            if (status) params.append('status', status);
            params.append('limit', limit.toString());
            params.append('skip', skip.toString());

            const response = await this.client.get(`/api/v1/user-transactions?${params.toString()}`);

            logger.log('✅ All user transactions fetched successfully:', {
                total: response.data.data?.total || 0,
                count: response.data.data?.count || 0,
                timestamp: new Date().toISOString(),
            });

            // Handle new response structure from backend
            return response.data.data?.transactions || [];
        } catch (error) {
            logger.error('💥 Error fetching user transactions:', {
                error: error instanceof Error ? error.message : String(error),
                user_email,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Create a new subscription
     * POST /api/subscriptions/
     */
    async createSubscription(data: SubscriptionCreate): Promise<Subscription> {
        try {
            logger.log('📝 Creating subscription...', {
                company_name: data.company_name,
                subscription_unit: data.subscription_unit,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post('/api/subscriptions/', data, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Subscription created successfully:', {
                subscription_id: response.data._id,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error creating subscription:', {
                error: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Update an existing subscription
     * PATCH /api/subscriptions/{subscription_id}
     */
    async updateSubscription(subscription_id: string, data: SubscriptionUpdate): Promise<Subscription> {
        try {
            logger.log('📝 UPDATE SUBSCRIPTION - REQUEST START:', {
                method: 'PATCH',
                url: `/api/subscriptions/${subscription_id}`,
                subscription_id,
                payload: data,
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.patch(`/api/subscriptions/${subscription_id}`, data, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ UPDATE SUBSCRIPTION - RESPONSE SUCCESS:', {
                subscription_id,
                status: response.status,
                statusText: response.statusText,
                data: response.data,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            const axiosError = error as any;
            logger.error('💥 UPDATE SUBSCRIPTION - ERROR:', {
                subscription_id,
                error: error instanceof Error ? error.message : String(error),
                response: {
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    data: axiosError.response?.data,
                    headers: axiosError.response?.headers,
                },
                request: {
                    url: axiosError.config?.url,
                    method: axiosError.config?.method,
                    data: axiosError.config?.data,
                },
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get subscriptions for a company
     * GET /api/subscriptions/company/{company_name}
     */
    async getCompanySubscriptions(company_name: string): Promise<Subscription[]> {
        try {
            logger.log('📊 Fetching company subscriptions...', {
                company_name,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.get(`/api/subscriptions/company/${company_name}`);

            // Extract subscriptions array from nested response structure
            // response.data.data gives us {company_name, count, subscriptions: [...]}
            const subscriptions = response.data.data.subscriptions || [];

            logger.log('✅ Company subscriptions fetched successfully:', {
                count: subscriptions.length,
                timestamp: new Date().toISOString(),
            });

            return subscriptions;
        } catch (error) {
            logger.error('💥 Error fetching company subscriptions:', {
                error: error instanceof Error ? error.message : String(error),
                company_name,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Delete a subscription (soft delete)
     * DELETE /api/subscriptions/{company_name}/{subscription_id}
     */
    async deleteSubscription(company_name: string, subscription_id: string): Promise<void> {
        try {
            logger.log('🗑️ Deleting subscription...', {
                company_name,
                subscription_id,
                timestamp: new Date().toISOString(),
            });

            await this.client.delete(`/api/subscriptions/${company_name}/${subscription_id}`);

            logger.log('✅ Subscription deleted successfully:', {
                subscription_id,
                timestamp: new Date().toISOString(),
            });
        } catch (error) {
            logger.error('💥 Error deleting subscription:', {
                error: error instanceof Error ? error.message : String(error),
                subscription_id,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get a single subscription by ID
     * GET /api/subscriptions/{subscription_id}
     */
    async getSubscription(subscription_id: string): Promise<Subscription> {
        try {
            logger.log('📄 Fetching subscription by ID...', {
                subscription_id,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.get(`/api/subscriptions/${subscription_id}`);

            logger.log('✅ Subscription fetched successfully:', {
                subscription_id,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error fetching subscription:', {
                error: error instanceof Error ? error.message : String(error),
                subscription_id,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Create a new company user
     * POST /api/company-users?company_name={name}
     */
    async getAllCompanyUsers(companyName?: string): Promise<CompanyUser[]> {
        try {
            logger.log('👥 Fetching company users...', {
                companyName: companyName || 'all',
                timestamp: new Date().toISOString(),
            });

            const url = companyName
                ? `/api/company-users?company_name=${encodeURIComponent(companyName)}`
                : '/api/company-users';

            const response = await this.client.get<CompanyUser[]>(url);

            logger.log('✅ Company users fetched successfully:', {
                count: response.data.length,
                companyName: companyName || 'all',
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error fetching company users:', {
                error: error instanceof Error ? error.message : String(error),
                companyName: companyName || 'all',
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get dashboard metrics (total revenue, active subscriptions, etc.)
     * Used by AdminDashboard component for overview statistics
     */
    async getDashboardMetrics(): Promise<{
        total_revenue: number;
        active_subscriptions: number;
        total_transactions: number;
        active_companies: number;
    }> {
        try {
            logger.log('📊 Fetching dashboard metrics...', {
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.get('/api/v1/dashboard/metrics');

            logger.log('✅ Dashboard metrics fetched successfully:', {
                metrics: response.data.data,
                timestamp: new Date().toISOString(),
            });

            return response.data.data;
        } catch (error) {
            logger.error('💥 Error fetching dashboard metrics:', {
                error: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    async createCompanyUser(companyName: string, userData: CompanyUserCreateRequest): Promise<CompanyUser> {
        try {
            logger.log('👥 Creating company user...', {
                companyName,
                user_name: userData.user_name,
                email: userData.email,
                permission_level: userData.permission_level,
                status: userData.status,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post<CompanyUser>(
                `/api/company-users?company_name=${encodeURIComponent(companyName)}`,
                userData,
                {
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                    },
                },
            );

            logger.log('✅ Company user created successfully:', {
                user_id: response.data.user_id,
                company_name: response.data.company_name,
                user_name: response.data.user_name,
                email: response.data.email,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error creating company user:', {
                error: error instanceof Error ? error.message : String(error),
                companyName,
                user_name: userData.user_name,
                email: userData.email,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Update an existing company user
     * PATCH /api/company-users/{user_id}
     */
    async updateCompanyUser(
        userId: string,
        userData: {
            user_name?: string;
            email?: string;
            phone_number?: string;
            password?: string;
            permission_level?: string;
            status?: string;
        },
    ): Promise<any> {
        try {
            logger.log('👤 Updating company user...', {
                userId,
                updates: { ...userData, password: userData.password ? '[REDACTED]' : undefined },
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.patch(`/api/company-users/${userId}`, userData, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Company user updated successfully:', {
                user_id: response.data.user_id,
                user_name: response.data.user_name,
                email: response.data.email,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error updating company user:', {
                error: error instanceof Error ? error.message : String(error),
                userId,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Delete a company user
     * DELETE /api/company-users/{user_id}
     */
    async deleteCompanyUser(userId: string): Promise<void> {
        try {
            logger.log('🗑️ Deleting company user...', {
                userId,
                timestamp: new Date().toISOString(),
            });

            await this.client.delete(`/api/company-users/${userId}`);

            logger.log('✅ Company user deleted successfully:', {
                userId,
                timestamp: new Date().toISOString(),
            });
        } catch (error) {
            logger.error('💥 Error deleting company user:', {
                error: error instanceof Error ? error.message : String(error),
                userId,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get documents and orders for the authenticated corporate user
     * @param filters - Optional filters for date period, language, status, search
     * @returns Promise with periods containing orders
     */
    async getDocumentsOrders(filters?: {
        datePeriod?: string;
        language?: string;
        status?: string;
        searchQuery?: string;
    }): Promise<{
        success: boolean;
        data?: any;
        error?: { message: string };
    }> {
        try {
            logger.log('📄 Fetching documents and orders...', {
                filters,
                timestamp: new Date().toISOString(),
            });

            // Build query parameters
            const params = new URLSearchParams();
            if (filters?.datePeriod) params.append('date_period', filters.datePeriod);
            if (filters?.language) params.append('language', filters.language);
            if (filters?.status) params.append('status', filters.status);
            if (filters?.searchQuery) params.append('search', filters.searchQuery);

            const queryString = params.toString();
            const url = `/api/orders${queryString ? `?${queryString}` : ''}`;

            const response = await this.client.get(url);

            logger.log('✅ Documents and orders fetched successfully:', {
                periodsCount: response.data?.data?.periods?.length || 0,
                totalOrders: response.data?.data?.totalOrders || 0,
                timestamp: new Date().toISOString(),
            });

            return {
                success: true,
                data: response.data.data,
            };
        } catch (error) {
            logger.error('💥 Error fetching documents and orders:', {
                error: error instanceof Error ? error.message : String(error),
                filters,
                timestamp: new Date().toISOString(),
            });

            return {
                success: false,
                error: {
                    message: error instanceof Error ? error.message : 'Failed to fetch documents and orders',
                },
            };
        }
    }

    /**
     * Create a new invoice
     * POST /api/v1/invoices
     */
    async createInvoice(invoiceData: {
        company_name: string;
        subscription_id: string;
        invoice_number: string;
        invoice_date: string;
        due_date: string;
        total_amount: number;
        tax_amount: number;
        status: 'sent' | 'paid' | 'overdue' | 'cancelled';
        pdf_url?: string;
    }): Promise<any> {
        try {
            logger.log('📝 Creating invoice...', {
                company_name: invoiceData.company_name,
                invoice_number: invoiceData.invoice_number,
                total_amount: invoiceData.total_amount,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post('/api/v1/invoices', invoiceData, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ Invoice created successfully:', {
                invoice_id: response.data.data?._id,
                invoice_number: response.data.data?.invoice_number,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error creating invoice:', {
                error: error instanceof Error ? error.message : String(error),
                company_name: invoiceData.company_name,
                invoice_number: invoiceData.invoice_number,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Update an existing invoice
     * PATCH /api/v1/invoices/{invoice_id}
     */
    async updateInvoice(
        invoice_id: string,
        invoiceData: {
            status?: 'sent' | 'paid' | 'overdue' | 'cancelled';
            invoice_date?: string;
            due_date?: string;
            total_amount?: number;
            tax_amount?: number;
            pdf_url?: string;
        },
    ): Promise<any> {
        try {
            logger.log('📝 UPDATE INVOICE - REQUEST START:', {
                method: 'PATCH',
                url: `/api/v1/invoices/${invoice_id}`,
                invoice_id,
                payload: invoiceData,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.patch(`/api/v1/invoices/${invoice_id}`, invoiceData, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            });

            logger.log('✅ UPDATE INVOICE - RESPONSE SUCCESS:', {
                invoice_id,
                status: response.status,
                statusText: response.statusText,
                data: response.data,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            const axiosError = error as any;
            logger.error('💥 UPDATE INVOICE - ERROR:', {
                invoice_id,
                error: error instanceof Error ? error.message : String(error),
                response: {
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    data: axiosError.response?.data,
                    headers: axiosError.response?.headers,
                },
                request: {
                    url: axiosError.config?.url,
                    method: axiosError.config?.method,
                    data: axiosError.config?.data,
                },
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Generate quarterly invoice for a subscription (Admin only)
     * POST /api/v1/invoices/generate-quarterly
     */
    async generateQuarterlyInvoice(subscriptionId: string, quarter: number): Promise<Invoice> {
        try {
            logger.log('📄 GENERATE QUARTERLY INVOICE - REQUEST START:', {
                method: 'POST',
                url: '/api/v1/invoices/generate-quarterly',
                subscriptionId,
                quarter,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post('/api/v1/invoices/generate-quarterly', null, {
                params: {
                    subscription_id: subscriptionId,
                    quarter: quarter,
                },
            });

            logger.log('✅ GENERATE QUARTERLY INVOICE - RESPONSE SUCCESS:', {
                status: response.status,
                statusText: response.statusText,
                invoiceNumber: response.data.data?.invoice_number,
                timestamp: new Date().toISOString(),
            });

            return response.data.data;
        } catch (error) {
            const axiosError = error as any;
            logger.error('💥 GENERATE QUARTERLY INVOICE - ERROR:', {
                subscriptionId,
                quarter,
                error: error instanceof Error ? error.message : String(error),
                response: {
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    data: axiosError.response?.data,
                },
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Generate monthly invoice for a subscription (Admin only)
     * POST /api/v1/invoices/generate-monthly
     */
    async generateMonthlyInvoice(subscriptionId: string, month: number): Promise<Invoice> {
        try {
            logger.log('📄 GENERATE MONTHLY INVOICE - REQUEST START:', {
                method: 'POST',
                url: '/api/v1/invoices/generate-monthly',
                subscriptionId,
                month,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post('/api/v1/invoices/generate-monthly', null, {
                params: {
                    subscription_id: subscriptionId,
                    month: month,
                },
            });

            logger.log('✅ GENERATE MONTHLY INVOICE - RESPONSE SUCCESS:', {
                status: response.status,
                statusText: response.statusText,
                invoiceNumber: response.data.data?.invoice_number,
                timestamp: new Date().toISOString(),
            });

            return response.data.data;
        } catch (error) {
            const axiosError = error as any;
            logger.error('💥 GENERATE MONTHLY INVOICE - ERROR:', {
                subscriptionId,
                month,
                error: error instanceof Error ? error.message : String(error),
                response: {
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    data: axiosError.response?.data,
                },
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Apply payment to invoice (Admin only)
     * POST /api/v1/payments/{payment_id}/apply-to-invoice
     */
    async applyPaymentToInvoice(paymentId: string, invoiceId: string): Promise<any> {
        try {
            logger.log('💳 APPLY PAYMENT TO INVOICE - REQUEST START:', {
                method: 'POST',
                url: `/api/v1/payments/${paymentId}/apply-to-invoice`,
                paymentId,
                invoiceId,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post(`/api/v1/payments/${paymentId}/apply-to-invoice`, null, {
                params: {
                    invoice_id: invoiceId,
                },
            });

            logger.log('✅ APPLY PAYMENT TO INVOICE - RESPONSE SUCCESS:', {
                status: response.status,
                statusText: response.statusText,
                invoiceStatus: response.data.data?.status,
                amountPaid: response.data.data?.amount_paid,
                timestamp: new Date().toISOString(),
            });

            return response.data.data;
        } catch (error) {
            const axiosError = error as any;
            logger.error('💥 APPLY PAYMENT TO INVOICE - ERROR:', {
                paymentId,
                invoiceId,
                error: error instanceof Error ? error.message : String(error),
                response: {
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    data: axiosError.response?.data,
                },
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Send invoice email with PDF attachment to customer
     * POST /api/v1/invoices/{invoice_id}/send-email
     */
    async sendInvoiceEmail(invoice_id: string): Promise<{
        success: boolean;
        message: string;
        invoice_id: string;
        payment_link_url?: string;
    }> {
        try {
            logger.log('📧 Sending invoice email...', {
                invoice_id,
                timestamp: new Date().toISOString(),
            });

            const response = await this.client.post(`/api/v1/invoices/${invoice_id}/send-email`);

            logger.log('✅ Invoice email sent successfully:', {
                invoice_id,
                timestamp: new Date().toISOString(),
            });

            return response.data;
        } catch (error) {
            logger.error('💥 Error sending invoice email:', {
                error: error instanceof Error ? error.message : String(error),
                invoice_id,
                timestamp: new Date().toISOString(),
            });
            throw error;
        }
    }

    /**
     * Get enterprise documents with pagination, search, and sorting
     * GET /api/enterprise/documents
     */
    async getEnterpriseDocuments(params: {
        search?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        pageSize?: number;
    }): Promise<any> {
        const queryParams = new URLSearchParams();
        if (params.search) queryParams.append('search', params.search);
        queryParams.append('sort_by', params.sortBy || 'date');
        queryParams.append('sort_order', params.sortOrder || 'desc');
        queryParams.append('skip', String(((params.page || 1) - 1) * (params.pageSize || 20)));
        queryParams.append('limit', String(params.pageSize || 20));

        const response = await this.client.get(`/api/enterprise/documents?${queryParams.toString()}`);
        return response.data;
    }

    /**
     * Get unpaid invoice for admin banner
     * GET /api/enterprise/invoice/unpaid
     */
    async getUnpaidInvoice(): Promise<any> {
        const response = await this.client.get('/api/enterprise/invoice/unpaid');
        return response.data;
    }

    /**
     * Get all invoices for enterprise company
     * GET /api/enterprise/invoices
     */
    async getEnterpriseInvoices(params: {
        search?: string;
        sortBy?: 'date' | 'amount' | 'due_date';
        sortOrder?: 'asc' | 'desc';
        page?: number;
        pageSize?: number;
    }): Promise<any> {
        const queryParams = new URLSearchParams();

        if (params.search) {
            queryParams.append('search', params.search);
        }

        queryParams.append('sort_by', params.sortBy || 'date');
        queryParams.append('sort_order', params.sortOrder || 'desc');
        queryParams.append('page', String(params.page || 1));
        queryParams.append('page_size', String(params.pageSize || 20));

        const response = await this.client.get(`/api/enterprise/invoices?${queryParams.toString()}`);
        return response.data;
    }

    // Send payment link to client via email
    async sendPaymentLink(params: {
        transaction_id: string;
        client_email: string;
        amount_cents: number;
    }): Promise<{ success: boolean; sent_to: string; expires_in_hours: number }> {
        try {
            const response = await this.client.post<{
                success: boolean;
                sent_to: string;
                expires_in_hours: number;
            }>('/api/payment/send-client-link', params);

            return response.data;
        } catch (error) {
            this.handlePaymentError(error, 'Send payment link');
        }
    }
}

const apiService = new ApiService();
export default apiService;
